import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import type { QInputProps } from 'quasar'
import { computed, type Ref, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAppStore } from '@/store/modules/app'
import type {
  Filter,
  FilterBreakpoint,
  FilterBtnGroup,
  FilterDummy,
  FilterInput,
  FilterInputRange,
  FilterInputRangeCurrency,
  FilterInputRangeDate,
  FilterOptionGroup,
  FilterSlider,
  FilterToggle,
  SPFilterProps,
} from '@/types/api/filter'
import type { IndexSearchVariables, ShowMapVariables } from '@/types/api/search'

const excludedFromApplyAllFilters = Object.freeze([
  'coordinates',
  'locations',
  'per_page',
  'sort_by',
  'zoom',
])

const excludedFromQuery = Object.freeze(['category', 'commercialization'])

const excludedFromClear = Object.freeze([
  'coordinates',
  'locations',
  'per_page',
  'sort_by',
  'zoom',
])

const excludedFromCount = Object.freeze([
  'coordinates',
  'locations',
  'page',
  'per_page',
  'sort_by',
  'zoom',
])

const excludeFromValidation = Object.freeze([
  'page',
  'per_page',
  'sort_by',
  'zoom',
])

const filters = ref<Filter[]>([])

const filtersLoading = ref<boolean>(false)

const filterValues = ref<Record<string, string[]>>({})

export const useFilter = (
  breakpoint?: Ref<FilterBreakpoint>,
  slot?: Ref<string>,
) => {
  const route = useRoute()
  const router = useRouter()

  const { configTheme } = storeToRefs(useAppStore())

  const appliedFiltersCount = computed(
    () =>
      Object.keys(route.query).filter((k) => !excludedFromCount.includes(k))
        .length,
  )

  const clearAllFilters = () => {
    const query: Record<string, string | undefined> = {}

    Object.keys(route.query)
      .filter((k) => excludedFromClear.includes(k))
      .forEach((k) => {
        query[k] = route.query[k]?.toString()
      })

    router.push({
      params: route.params,
      query,
    })
  }

  const filtersLocal = computed<SPFilterProps[]>(() =>
    breakpoint?.value && slot?.value
      ? // @ts-expect-error instantiation
        (filters.value.filter(
          (f) =>
            f.component !== 'FilterDummy'
            && f.breakpoint === breakpoint.value
            && f.slot === slot.value,
        ) as SPFilterProps[])
      : [],
  )

  const validateQueryValue = (
    type: QInputProps['type'],
    queryValue: string,
  ) => {
    switch (type) {
      case 'number':
        return Number.isNaN(Number(queryValue)) ? null : queryValue

      default:
        return queryValue
    }
  }

  const validateFilterWithInput = (filter: FilterInput, queryValue: string) =>
    validateQueryValue(filter.componentProps.inputProps.type, queryValue)

  const validateFilterWithInputRange = (
    filter: FilterInputRange | FilterInputRangeCurrency,
    queryValue: string,
  ) => {
    const separator = ','

    const queryValues = queryValue.split(separator)

    const from = validateQueryValue('number', queryValues[0])

    const to = validateQueryValue('number', queryValues[1])

    const qV = []

    if (from !== null) {
      qV.push(from)
    }

    if (to !== null) {
      qV.push(to)
    }

    return [from, to].sort((a, b) => Number(a) - Number(b)).join(separator)
  }

  const validateFilterWithInputRangeDate = (
    filter: FilterInputRangeDate,
    queryValue: string,
  ): string => {
    const separator = ','

    const [fromRaw, toRaw] = queryValue.split(separator).map((v) => v.trim())

    const isValidDate = (date: string) =>
      dayjs(date, 'YYYY-MM-DD', true).isValid()

    const from = isValidDate(fromRaw) ? fromRaw : ''
    const to = isValidDate(toRaw) ? toRaw : ''

    if (from && to) {
      return [from, to].join(separator)
    }

    return ''
  }

  const validateFilterWithOptions = (
    filter: FilterBtnGroup | FilterOptionGroup,
    queryValue: string,
  ) => {
    const separator = ','

    const isMultiple = filter.componentProps.multiple.enabled

    const queryValues = isMultiple ? queryValue.split(separator) : [queryValue]

    const validatedFilters = queryValues.filter(
      (v) =>
        filter.componentProps.options.findIndex((o) => o.value === v) !== -1,
    )

    const qV = validatedFilters.join(separator)

    return qV.length ? qV : ''
  }

  const validateFilterSlider = (filter: FilterSlider, queryValue: string) => {
    return filter.componentProps.markerLabels.find(
      (mL) => `${mL.value}` === queryValue,
    )?.value
  }

  const validateFilterToggle = (filter: FilterToggle, queryValue: string) => {
    return (
      [filter.componentProps.falseValue, filter.componentProps.trueValue].find(
        (v) => v.value === queryValue,
      )?.value ?? null
    )
  }

  const validateCoordinates = (_filter: Filter, queryValue: string) => {
    const values = queryValue.split(',')

    const condition =
      values.every((v) => !Number.isNaN(Number(v))) && values.length === 8

    return condition ? queryValue : ''
  }

  const validateUrlQueryParamValues = (filter: Filter, queryValue: string) => {
    switch (filter.component) {
      case 'FilterBtnGroup':
      case 'FilterOptionGroup':
        return validateFilterWithOptions(filter, queryValue)

      case 'FilterDummy':
        return queryValue

      case 'FilterCoordinates':
        return validateCoordinates(filter, queryValue)

      case 'FilterInput':
        return validateFilterWithInput(filter, queryValue)

      case 'FilterLocations':
      case 'FilterLocationsDialog':
        return queryValue // TODO: validation?

      case 'FilterInputRange':
      case 'FilterInputRangeCurrency':
        return validateFilterWithInputRange(filter, queryValue)

      case 'FilterInputRangeDate':
        return validateFilterWithInputRangeDate(filter, queryValue)

      case 'FilterSlider':
        return validateFilterSlider(filter, queryValue)

      case 'FilterToggle':
        return validateFilterToggle(filter, queryValue)

      default:
        console.warn(`Unsupported filter component: ${filter.slug}`)
        return null
    }
  }

  const getQueryValueFromDummy = (
    filter: FilterDummy,
    urlValue: string,
  ): string => {
    const isMultiple = filter.componentProps.multiple.enabled

    const v = urlValue.split(',').map((value) => `${filter.slug}:=${value}`)

    return isMultiple
      ? `(${v.join(filter.componentProps.multiple.join_operator)})`
      : urlValue
  }

  const stripInvalidUrlQueryParams = () => {
    const routeQuery = { ...route.query }

    Object.keys(routeQuery).forEach((k) => {
      const filter = filters.value.find((f) => f.slug === k)

      if (!filter && !excludeFromValidation.includes(k)) {
        delete routeQuery[k]
      } else if (filter) {
        const routeQueryValue = routeQuery[k]?.toString() || ''
        const routeQueryValueValidated = `${validateUrlQueryParamValues(filter, routeQueryValue)}`

        if ([null, undefined, ''].includes(routeQueryValueValidated)) {
          console.warn(`invalid ${k} with value: ${routeQuery[k]}`)

          delete routeQuery[k]
        } else {
          routeQuery[k] = routeQueryValueValidated
        }
      }
    })

    if (JSON.stringify(route.query) !== JSON.stringify(routeQuery)) {
      console.warn('stripInvalidUrlQueryParams: invalid filters')

      router.replace({
        params: route.params,
        query: routeQuery,
      })
    }
  }

  const getQueryValueFromOptions = (
    filter: FilterBtnGroup | FilterOptionGroup,
    urlValue: string,
  ): string => {
    const isMultiple = filter.componentProps.multiple.enabled

    if (isMultiple) {
      const vFilterBtnGroup: string[] = []

      urlValue.split(',').forEach((_v) => {
        const vS = filter.componentProps.options.find(
          (o) => o.value === _v,
        )?.value_search

        if (vS) {
          vFilterBtnGroup.push(vS)
        }
      })

      return `(${vFilterBtnGroup.join(filter.componentProps.multiple.join_operator)})`
    }

    return (
      filter.componentProps.options.find((o) => o.value === urlValue)
        ?.value_search || ''
    )
  }

  const getQueryValueFromSlider = (filter: FilterSlider, urlValue: string) => {
    console.warn(filter.slug, urlValue)

    if (filter.componentProps.markerLabels?.length) {
      return (
        filter.componentProps.markerLabels.find(
          (o) => o.value === Number(urlValue),
        )?.value_search || ''
      )
    }

    console.log(
      'TODO: getQueryValueFromSlider resolve url value without marker labels',
    )

    return ''
  }

  const getQueryValue = (slug: string, urlValue: string): string => {
    const filter: Filter | undefined = filters.value.find(
      (f) => f.slug === slug,
    )

    if (!filter) return ''

    switch (filter.component) {
      case 'FilterBtnGroup':
      case 'FilterOptionGroup':
        return getQueryValueFromOptions(filter, urlValue)

      case 'FilterCategory':
      case 'FilterCategoryAccordion':
      case 'FilterCommercialization':
      case 'FilterCommercializationAccordion':
        return (
          filter.componentProps.options.find((o) => o.value === urlValue)
            ?.value_search || ''
        )

      case 'FilterCoordinates':
        const v = urlValue.replaceAll(',', ', ')
        return `${slug}: (${v})`

      case 'FilterDummy':
        return getQueryValueFromDummy(filter, urlValue)

      case 'FilterLocations':
      case 'FilterLocationsDialog':
        return `${slug}:=[${urlValue.replaceAll(',', ', ')}]`

      case 'FilterInput':
        return `${slug}${filter.componentProps.operator}${urlValue}`

      case 'FilterInputRange':
      case 'FilterInputRangeCurrency':
        const vIR: string[] = []

        const [from, to] = urlValue.split(',')

        if (![null, undefined, ''].includes(from)) {
          vIR.push(`${slug}:>=${from}`)
        }

        if (![null, undefined, ''].includes(to)) {
          vIR.push(`${slug}:<=${to}`)
        }
        return vIR.join(' && ')

      case 'FilterInputRangeDate':
        const vIRD: string[] = []

        const [fromDate, toDate] = urlValue.split(',')

        if (![null, undefined, ''].includes(fromDate)) {
          vIRD.push(
            `${slug}:>=${Math.floor(new Date(fromDate).getTime() / 1000)}`,
          )
        }

        if (![null, undefined, ''].includes(toDate)) {
          vIRD.push(
            `${slug}:<=${Math.floor(new Date(toDate).getTime() / 1000)}`,
          )
        }

        return vIRD.join(' && ')

      case 'FilterSlider':
        return getQueryValueFromSlider(filter, urlValue)

      case 'FilterToggle':
        return (
          [
            filter.componentProps.trueValue,
            filter.componentProps.falseValue,
          ].find((v) => v.value === urlValue)?.value_search || ''
        )

      default:
        return ''
    }
  }

  const getFilterValue = (filter: Filter, v: (number | string)[]): string => {
    let value: string = ''

    switch (filter.component) {
      case 'FilterBtnGroup':
      case 'FilterCoordinates':
      case 'FilterInputRange':
      case 'FilterInputRangeCurrency':
      case 'FilterInputRangeDate':
      case 'FilterLocations':
      case 'FilterLocationsDialog':
      case 'FilterOptionGroup':
        value = v.join(',')
        break

      case 'FilterInput':
      case 'FilterToggle':
        value = v[0]?.toString() || ''
        break

      case 'FilterSlider':
        value = Number.isNaN(Number(v[0]?.toString())) ? '' : v[0].toString()
        break
    }

    return value
  }

  const applyAllFilters = () => {
    const query: Record<string, string | undefined> = {
      page: '1',
    }

    filters.value
      .filter((f) => !excludedFromQuery.includes(f.slug))
      .forEach((f) => {
        const value = getFilterValue(f, filterValues.value[f.slug] || [])

        if (![undefined, null, ''].includes(value)) {
          query[f.slug] = value
        }
      })

    Object.keys(route.query)
      .filter((k) => excludedFromApplyAllFilters.includes(k))
      .forEach((k) => {
        query[k] = route.query[k]?.toString()
      })

    router.push({
      params: route.params,
      query,
    })
  }

  const applySingleFilter = (
    filter: Filter,
    v: (number | string)[],
    queryOverride: Record<string, string | number | null | undefined> = {},
  ) => {
    const value = getFilterValue(filter, v)

    const query: Record<string, string | number | null | undefined> = {
      ...route.query,
      page: 1,
      ...queryOverride,
      [filter.slug]: value,
    }

    if (!value.length) {
      delete query[filter.slug]
    }

    router.push({
      params: route.params,
      query,
    })
  }

  const getSearchVariables = () => {
    const v = { ...route.query }

    const page = Number.parseInt(v.page?.toString() || '', 10)

    const sortBy = v.sort_by?.toString()

    const category = getQueryValue(
      'category',
      route.params.category?.toString() || '',
    )

    const commercialization = getQueryValue(
      'commercialization',
      route.params.commercialization?.toString() || '',
    )

    const filterBy: string[] = []

    if (category) {
      filterBy.push(category)
    }

    if (commercialization) {
      filterBy.push(commercialization)
    }

    Object.keys(v).forEach((slug) => {
      const f = getQueryValue(slug, v[slug]?.toString() || '')

      if (f?.length) {
        filterBy.push(f)
      }
    })

    const variables: IndexSearchVariables = {
      ...(filterBy.length ? { filter_by: filterBy.join(' && ') } : {}),
      page: 1,
      sort_by: '-default_sort,-created_at',
    }

    const perPage = configTheme.value?.page.search.meta.per_page.search

    if (perPage) {
      variables.per_page = perPage
    }

    if (!Number.isNaN(page) && page > 0) {
      variables.page = page
    }

    if (sortBy) {
      variables.sort_by = sortBy
    }

    return variables
  }

  const getMapVariables = (): ShowMapVariables => {
    const variables = getSearchVariables()

    const perPageMapMarkers =
      configTheme.value?.page.search.meta.per_page.map_markers

    const perPageSearch = variables.per_page
    const pageSearch = variables.page

    let pageMapMarkers = 1

    if (perPageMapMarkers) {
      if (perPageSearch) {
        pageMapMarkers = Math.ceil(
          (pageSearch * perPageSearch) / perPageMapMarkers,
        )

        if (pageMapMarkers < 1) {
          pageMapMarkers = 1
        }
      }

      variables.per_page = perPageMapMarkers

      variables.page = pageMapMarkers
    }

    const zoom = Number(route.query.zoom?.toString())

    return {
      ...variables,
      zoom,
    }
  }

  return {
    appliedFiltersCount,
    applyAllFilters,
    applySingleFilter,
    clearAllFilters,
    filterValues,
    filters,
    filtersLoading,
    filtersLocal,
    getFilterValue,
    getMapVariables,
    getQueryValue,
    getSearchVariables,
    stripInvalidUrlQueryParams,
  }
}
